import { call, delay, put, select, takeLatest } from 'redux-saga/effects';
import axios from 'axios';
import get from 'lodash.get';
import { stopSubmit } from 'redux-form';

// Global Types
import { SagaIterator } from 'redux-saga';

// Constants
import {
    CONTAINER_GROUP_LOAD_REQUESTED,
    EVENT_LABELS_CREATE_REQUESTED,
    EVENT_LABELS_CANCEL_REQUESTED
} from './constants';

// Actions
import {
    succeededLoadContainerGroup,
    failedLoadContainerGroup,
    succeededEventPrintLabels,
    failedEventPrintLabels,
    setContainerGroup,
    succeededEventCancelLabels,
    failedEventCancelLabels
} from './actions';
import { updateQueryString } from '../router/actions';
import { setHarvestEvent } from '../harvestEvents/actions';

// Selectors
// @ts-ignore
import { idTokenSelector } from '../auth/selectors';
import { makeSelectHarvestEvent } from '../harvestEvents/selectors';

// Types
import {
    RequestedContainerGroupAction,
    RequestedPrintLabelsAction,
    RequestedCancelLabelsAction
} from './types';

// Environment
const { REACT_APP_API_URL } = process.env;

export function* getContainerGroup(
    action: RequestedContainerGroupAction
): SagaIterator {
    const {
        payload: { containerGroupId }
    } = action;

    const idToken = yield select(idTokenSelector);

    const url = `${REACT_APP_API_URL}/v1/ContainerGroups/${containerGroupId}`;

    const config = {
        headers: {
            Authorization: idToken
        }
    };

    let previousContainerGroup;

    while (!previousContainerGroup || !previousContainerGroup.isComplete) {
        yield delay(5000); // 5 seconds
        try {
            const response = yield call(axios.get, url, config);
            const { printingPayload, ...containerGroup } = response.data;

            const harvestEventUuid = get(printingPayload, ['event', 'uuid']);
            const harvestEventProperties = get(printingPayload, [
                'event',
                'harvestEvent'
            ]);

            const harvestEvent = yield select(makeSelectHarvestEvent(), {
                harvestEventUuid
            });

            // Create a deep clone of the harvestEvent
            const nextHarvestEvent = JSON.parse(JSON.stringify(harvestEvent));

            if (nextHarvestEvent && harvestEventProperties) {
                // Set Harvest Event
                yield put(
                    setHarvestEvent({
                        ...nextHarvestEvent,
                        harvestEvent: {
                            ...nextHarvestEvent.harvestEvent,
                            colorGrade:
                                harvestEventProperties.colorGrade || null,
                            harvestBinSuitabilityId:
                                harvestEventProperties.harvestBinSuitabilityId,
                            harvestGrowingMethodId:
                                harvestEventProperties.harvestGrowingMethodId,
                            harvestLocationId:
                                harvestEventProperties.harvestLocationId,
                            harvestPalletTypeId:
                                harvestEventProperties.harvestPalletTypeId,
                            harvestStorageTypeId:
                                harvestEventProperties.harvestStorageTypeId,
                            harvestSubmissionProfileId:
                                harvestEventProperties.harvestSubmissionProfileId
                        }
                    })
                );
            }

            // Set Container Group
            yield put(setContainerGroup(containerGroup));

            // Succeeded Load Container Group
            yield put(succeededLoadContainerGroup(response));

            previousContainerGroup = containerGroup;

            const { errors } = containerGroup;

            if (errors && errors.length) {
                // Redux form error
                yield put(
                    stopSubmit('eventPrintForm', { _error: errors.join(', ') })
                );
            } else {
                // Update Query String
                yield put(
                    updateQueryString({
                        selectedContainerGroup: previousContainerGroup.id
                    })
                );
            }
        } catch (error) {
            // Failed Load Container Group
            yield put(failedLoadContainerGroup(error));
        }
    }
}

export function* watchContainerGroupLoadRequested(): SagaIterator {
    yield takeLatest(CONTAINER_GROUP_LOAD_REQUESTED, getContainerGroup);
}

export function* eventPrintLabels(
    action: RequestedPrintLabelsAction
): SagaIterator {
    const {
        payload: { eventId, data }
    } = action;

    const idToken = yield select(idTokenSelector);

    const url = `${REACT_APP_API_URL}/v1/Events/${eventId}/printLabels`;

    const config = {
        headers: {
            Authorization: idToken
        }
    };

    try {
        const response = yield call(axios.post, url, data, config);
        const { printingPayload, ...containerGroup } = response.data;

        // Set Container Group
        yield put(setContainerGroup(containerGroup));

        // Succeeded Event Print Labels
        yield put(succeededEventPrintLabels(response));
    } catch (error) {
        // Failed Event Print Labels
        yield put(failedEventPrintLabels(error));

        const message = get(
            error,
            'response.data.message',
            'There was an error. Pleasze try again later.'
        );

        // Redux form error
        yield put(stopSubmit('eventPrintForm', { _error: message }));
    }
}

export function* watchEventPrintLabelsRequested(): SagaIterator {
    yield takeLatest(EVENT_LABELS_CREATE_REQUESTED, eventPrintLabels);
}

export function* eventCancelLabels(
    action: RequestedCancelLabelsAction
): SagaIterator {
    const {
        payload: { eventId }
    } = action;

    const idToken = yield select(idTokenSelector);

    const url = `${REACT_APP_API_URL}/v1/Events/${eventId}/printLabels`;

    const config = {
        headers: {
            Authorization: idToken
        }
    };

    try {
        const response = yield call(axios.delete, url, config);

        // Succeeded Event Cancel Labels
        yield put(succeededEventCancelLabels(response));
    } catch (error) {
        // Failed Event Cancel Labels
        yield put(failedEventCancelLabels(error));

        const message = get(
            error,
            'response.data.message',
            'There was an error. Please try again later.'
        );

        // Redux form error
        yield put(stopSubmit('eventPrintForm', { _error: message }));
    }
}

export function* watchEventCancelLabelsRequested(): SagaIterator {
    yield takeLatest(EVENT_LABELS_CANCEL_REQUESTED, eventCancelLabels);
}
